import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { db } from "../../firebaseConfig";
import {
  collection,
  getDocs,
  query,
  where,
  limit,
  doc,
  getDoc,
} from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import ProfileSetupClassCard from "../../components/ProfileSetupClassCard";
import ProfileSetupGroupCard from "../../components/ProfileSetupGroupCard";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/LanguageContext";

const ProfileSetup = () => {
  const [currentView, setCurrentView] = useState("groups");
  const [groups, setGroups] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joinedGroups, setJoinedGroups] = useState([]);
  const [joinedClasses, setJoinedClasses] = useState([]);
  const [isProcessingBooking, setIsProcessingBooking] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();

  useEffect(() => {
    const fetchTopContent = async () => {
      setLoading(true);
      setError(null);
      try {
        // Get user's learning language
        const userLearningLanguage =
          user?.learningLanguage || currentLanguage || "English";

        // Fetch classes filtered by user's learning language and English-Spanish
        const classesQuery = query(
          collection(db, "classes"),
          where("availableSpots", ">", 0),
        );
        const classesSnapshot = await getDocs(classesQuery);
        const allClasses = classesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter classes to only show available, future classes in user's learning language and English-Spanish
        const filteredClasses = allClasses
          .filter((classItem) => {
            const classLanguage = classItem.language?.toLowerCase() || "";
            const userLang = userLearningLanguage.toLowerCase();

            // Check if class is available (not full)
            const isAvailable = classItem.isAvailable !== false;

            // Check if class has space using availableSpots field
            const hasSpace = classItem.availableSpots > 0;

            // Check if class is in the future using classDateTime field
            const now = new Date();
            const classDate = classItem.classDateTime
              ? classItem.classDateTime.toDate
                ? classItem.classDateTime.toDate()
                : new Date(classItem.classDateTime)
              : null;
            const isFuture = classDate ? classDate > now : true;

            // Language filtering - show classes in user's learning language + english-spanish
            const languageMatch =
              classLanguage.includes(userLang) ||
              classLanguage.includes("english-spanish");

            return isAvailable && hasSpace && isFuture && languageMatch;
          })
          .slice(0, 3); // Take first 3 after filtering

        // Enrich classes with admin/tutor data if missing
        const enrichedClasses = await Promise.all(
          filteredClasses.map(async (classItem) => {
            // If admin data is missing, try to fetch it from tutors collection
            if (
              classItem.adminId &&
              (!classItem.adminName || !classItem.adminImageUrl)
            ) {
              try {
                const tutorDoc = await getDoc(
                  doc(db, "tutors", classItem.adminId),
                );
                if (tutorDoc.exists()) {
                  const tutorData = tutorDoc.data();
                  return {
                    ...classItem,
                    adminName: tutorData.name || classItem.adminName,
                    adminImageUrl:
                      tutorData.photoUrl || classItem.adminImageUrl,
                  };
                } else {
                  console.log("Tutor doc does not exist");
                }
              } catch (error) {
                console.log("Error fetching admin/tutor data:", error);
              }
            }
            return classItem;
          }),
        );

        setClasses(enrichedClasses);

        // Helper function to check if a class has available spots
        const hasAvailableClass = (classData) => {
          if (!classData) return false;
          const now = new Date();
          let classDate = null;
          if (classData.classDateTime) {
            classDate =
              typeof classData.classDateTime.toDate === "function"
                ? classData.classDateTime.toDate()
                : new Date(classData.classDateTime);
          }
          const isFuture = !classDate || classDate >= now;
          const availableSpots =
            typeof classData.availableSpots === "number"
              ? classData.availableSpots
              : Math.max(
                  0,
                  (classData.maxStudents || Infinity) -
                    (classData.classMemberIds?.length || 0),
                );
          const hasSpace = availableSpots > 0;
          const isExplicitlyUnavailable = classData.isAvailable === false;
          return !isExplicitlyUnavailable && hasSpace && isFuture;
        };

        // Fetch groups filtered by user's learning language
        const groupsQuery = query(
          collection(db, "groups"),
          limit(50), // Fetch more to filter client-side
        );

        const groupsSnapshot = await getDocs(groupsQuery);
        const allGroups = groupsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter groups by language first
        const languageFilteredGroups = allGroups.filter((group) => {
          // Try multiple possible language field names
          const groupLanguage = (
            group.language ||
            group.groupLearningLanguage ||
            group.learningLanguage ||
            ""
          ).toLowerCase();
          const userLang = userLearningLanguage.toLowerCase();

          // Language filtering - show groups in user's learning language + english-spanish
          const languageMatch =
            groupLanguage === userLang ||
            groupLanguage.includes(userLang) ||
            groupLanguage.includes("english-spanish") ||
            userLang.includes(groupLanguage);

          return languageMatch;
        });

        // Check availability of classes for each group
        const availabilityChecks = await Promise.all(
          languageFilteredGroups.map(async (group) => {
            if (!Array.isArray(group.classIds) || group.classIds.length === 0) {
              return null;
            }

            try {
              const classDocs = await Promise.all(
                group.classIds.map((classId) =>
                  getDoc(doc(db, "classes", classId)),
                ),
              );

              const hasAvailableClasses = classDocs.some((classDoc) => {
                if (!classDoc.exists()) {
                  return false;
                }
                return hasAvailableClass(classDoc.data());
              });

              return hasAvailableClasses ? group : null;
            } catch (error) {
              console.error(
                `Error checking classes for group ${group.id}:`,
                error,
              );
              return null;
            }
          }),
        );

        const filteredGroups = availabilityChecks.filter(Boolean).slice(0, 3);

        setGroups(filteredGroups);
      } catch (error) {
        console.error("Error fetching content:", error);
        setError("Failed to load content. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchTopContent();
  }, [user, currentLanguage]);

  const handleSkip = () => {
    // Navigate to learn and include language preference
    navigate("/learn", {
      state: {
        fromSetup: true,
        language: currentLanguage,
      },
    });
  };

  const handleNext = () => {
    if (currentView === "groups") {
      setCurrentView("classes");
    } else if (currentView === "classes") {
      navigate("/learn", {
        state: {
          fromSetup: true,
          language: currentLanguage,
        },
      });
    }
  };

  // Handle language change with context only
  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
  };

  if (loading || isProcessingBooking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <ClipLoader color="#14B82C" size={50} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-100">
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-full border border-[#5d5d5d] bg-[#e6fde9] px-4 py-2 text-base font-medium text-[#042f0c] hover:bg-[#ccfcd2]"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-gray-100">
      {/* Full-page loading overlay during booking */}

      {/* Language Selector */}
      <div className="absolute right-4 top-4">
        <select
          value={currentLanguage}
          onChange={(e) => handleLanguageChange(e.target.value)}
          className="rounded-full border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
        >
          <option value="en">English</option>
          <option value="es">Español</option>
        </select>
      </div>

      <div className="mx-auto w-full max-w-5xl px-4 py-8">
        <h1 className="mb-2 text-center text-4xl font-bold">
          {currentView === "groups"
            ? t("profileSetup.title", "Start Speaking Today")
            : currentView === "classes"
              ? t(
                  "profileSetup.step2Title",
                  "Get Fluent, Faster with Live Conversation",
                )
              : t("profileSetup.step3Title", "Meet Your Perfect Tutor!")}
        </h1>
        <p className="mb-8 text-center text-xl text-gray-600">
          {currentView === "groups"
            ? t(
                "profileSetup.subtitle",
                "Joining a language group is the best way to practice conversation. We have free groups and premium tutor-led groups.",
              )
            : currentView === "classes"
              ? t(
                  "profileSetup.step2Subtitle",
                  "We have free and premium classes. They are live and virtual and a great way to learn and practice conversation.",
                )
              : t(
                  "profileSetup.step3Subtitle",
                  "Ready to accelerate your learning? Find and connect with expert tutors for group conversation classes. It's the fastest way to build confidence and master difficult concepts.",
                )}
        </p>

        {/* Grid of cards */}
        <div className="mb-4 grid grid-cols-1 items-stretch gap-4 md:grid-cols-3">
          {currentView === "groups" ? (
            groups.length > 0 ? (
              groups.map((group) => (
                <ProfileSetupGroupCard
                  key={group.id}
                  group={group}
                  onJoin={(groupId) =>
                    setJoinedGroups((prev) => [...prev, groupId])
                  }
                  isJoined={
                    joinedGroups.includes(group.id) ||
                    (user?.joinedGroups && user.joinedGroups.includes(group.id))
                  }
                  setIsProcessingBooking={setIsProcessingBooking}
                />
              ))
            ) : (
              <div className="col-span-3 text-center text-gray-500">
                {t(
                  "profileSetup.noGroups",
                  "No groups available to join at the moment.",
                )}
              </div>
            )
          ) : classes.length > 0 ? (
            classes.map((classItem) => (
              <ProfileSetupClassCard
                key={classItem.id}
                {...classItem}
                isBammbuu={Boolean(classItem.tutorId)}
                onBook={(classId) =>
                  setJoinedClasses((prev) => [...prev, classId])
                }
                isBooked={
                  joinedClasses.includes(classItem.id) ||
                  (user?.enrolledClasses &&
                    user.enrolledClasses.includes(classItem.id))
                }
                setIsProcessingBooking={setIsProcessingBooking}
              />
            ))
          ) : (
            <div className="col-span-3 text-center text-gray-500">
              {t(
                "profileSetup.noClasses",
                "No classes available to book at the moment.",
              )}
            </div>
          )}
        </div>

        {/* Positioned tooltip based on current view */}
        <div
          className={`flex ${
            currentView === "groups"
              ? "justify-start"
              : currentView === "classes"
                ? "justify-center"
                : "justify-end"
          } mb-4 mt-8`}
        >
          <div className="relative">
            <div className="max-w-md rounded-2xl bg-[#042F0C] p-5 text-white">
              <h3 className="mb-2 text-sm font-medium">
                {currentView === "groups"
                  ? t(
                      "profileSetup.tooltips.joinGroups.title",
                      "Join 1 or more language learning groups.",
                    )
                  : t(
                      "profileSetup.tooltips.bookClasses.title",
                      "Book 1 or more classes.",
                    )}
              </h3>
              {(() => {
                if (currentView === "classes") {
                  // Try to get the points array with explicit fallback
                  const fallbackPoints = [
                    "Community Classes (Free): Join classes hosted by fellow students to practice speaking.",
                    "Tutor-Led Classes (Premium): Book a class with a certified tutor to start your free 7-day trial.",
                  ];

                  const bookClassesPoints =
                    t("profileSetup.tooltips.bookClasses.points", {
                      returnObjects: true,
                    }) || fallbackPoints;

                  // Check if we got a valid array
                  const isValidArray =
                    Array.isArray(bookClassesPoints) &&
                    bookClassesPoints.length > 0 &&
                    typeof bookClassesPoints[0] === "string";

                  if (isValidArray) {
                    return (
                      <ul className="mb-4 space-y-2 text-sm">
                        {bookClassesPoints.map((point, index) => (
                          <li key={index} className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  }
                }

                // Fallback to description
                return (
                  <p className="mb-4 text-sm">
                    {currentView === "groups"
                      ? t(
                          "profileSetup.tooltips.joinGroups.description",
                          "Description: Make the most out of bammbuu. Community language groups are free to create and join. They allow you to connect with native speakers to practice language through live conversation.",
                        )
                      : t(
                          "profileSetup.tooltips.bookClasses.description",
                          "Book unlimited live group conversation classes hosted by certified language instructors for one monthly price. These classes are more structured and expert feedback is provided to help with your learning.",
                        )}
                  </p>
                );
              })()}
              <div className="flex items-center justify-between">
                <button
                  onClick={handleSkip}
                  className="text-white hover:underline"
                >
                  {t("profileSetup.buttons.skip", "Skip")}
                </button>
                <button
                  onClick={handleNext}
                  disabled={
                    (currentView === "groups" &&
                      joinedGroups.length === 0 &&
                      (!user?.joinedGroups ||
                        user.joinedGroups.length === 0)) ||
                    (currentView === "classes" &&
                      joinedClasses.length === 0 &&
                      (!user?.enrolledClasses ||
                        user.enrolledClasses.length === 0))
                  }
                  className={`rounded-full px-4 py-1 ${
                    (currentView === "groups" &&
                      joinedGroups.length === 0 &&
                      (!user?.joinedGroups ||
                        user.joinedGroups.length === 0)) ||
                    (currentView === "classes" &&
                      joinedClasses.length === 0 &&
                      (!user?.enrolledClasses ||
                        user.enrolledClasses.length === 0))
                      ? "cursor-not-allowed bg-gray-300 text-gray-500"
                      : "bg-white text-[#043D11] hover:bg-opacity-90"
                  }`}
                >
                  {currentView === "groups"
                    ? t("profileSetup.buttons.nextStep", "Next (1/2)")
                    : t("profileSetup.buttons.nextStep2", "Next (2/2)")}
                </button>
              </div>
            </div>
            {/* Notch positioned to correspond with the correct card */}
            <div
              className={`absolute -top-2 ${
                currentView === "groups"
                  ? "left-1/4 -translate-x-1/2"
                  : "right-1/4 translate-x-1/2"
              }`}
            >
              <div className="h-0 w-0 border-b-8 border-l-8 border-r-8 border-transparent border-b-[#042F0C]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetup;
